import React from 'react';
import { useIntl } from 'react-intl';
import clsx from 'clsx';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck } from '@fortawesome/pro-light-svg-icons/faCheck';
import { faTriangleExclamation } from '@fortawesome/pro-light-svg-icons/faTriangleExclamation';
import { TypedMemo } from '../../common/types';
import { SMLButton } from '../../music24/components/primitives/SMLButton';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the whole profile editor; each part uses its own slice of it */
import styles from './SMLProfile.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface ProfileSaveBarProps {
  dirtyCount: number;
  isAddressDirty: boolean;
  canSave: boolean;
  isSaving: boolean;
  onDiscard: () => void;
  onSave: () => void;
  /** The button text when the address did not change. */
  saveLabel: string;
}

/** What is unsaved, and the two things you can do about it (design artist-settings a1, b1). */
const ProfileSaveBarComponent: React.FC<ProfileSaveBarProps> = ({
  dirtyCount,
  isAddressDirty,
  canSave,
  isSaving,
  onDiscard,
  onSave,
  saveLabel,
}) => {
  const intl = useIntl();
  const isDirty = dirtyCount > 0;

  return (
    <div className={clsx(styles.saveBar, !isDirty && styles.saveBarClean)}>
      <span className={styles.saveBarText}>
        <FontAwesomeIcon icon={isDirty ? faTriangleExclamation : faCheck} />
        {isDirty
          ? intl.formatMessage(
              {
                id: 'app.sml.profile.save.dirty',
                defaultMessage: '{count, plural, one {# unsaved change} other {# unsaved changes}}',
              },
              { count: dirtyCount }
            )
          : intl.formatMessage({ id: 'app.sml.profile.save.clean', defaultMessage: 'All changes saved' })}
      </span>
      {isDirty && (
        <SMLButton onClick={onDiscard} disabled={isSaving}>
          {intl.formatMessage({ id: 'app.sml.profile.save.discard', defaultMessage: 'Discard' })}
        </SMLButton>
      )}
      <SMLButton variant="primary" onClick={onSave} disabled={!canSave || isSaving}>
        {isAddressDirty
          ? intl.formatMessage({
              id: 'app.sml.profile.save.address',
              defaultMessage: 'Save and move pages',
            })
          : saveLabel}
      </SMLButton>
    </div>
  );
};

export const ProfileSaveBar = TypedMemo(ProfileSaveBarComponent);
